import {
  areIntervalsOverlapping,
  addSeconds,
  subSeconds,
  parseISO,
} from 'date-fns'

/*
 * The MomentFilter allows you to pass it an array of moments and
 * an object of all selected filters and it will return an array
 * of moments that match the selected filters.
 *
 * Some filters are inclusive, meaning moments or their content
 * can match any, and other filters are exclusive, meaning moments
 * or their content must match all.
 *
 * The following example illustrates how the selectedFilters
 * object should be structured. Labels are unique in that they
 * are all part of the `labels` entity attribute, but are
 * exclusive across different label sets, so they need to be
 * separated.
 *
 * selectedFilters: {
 *   account: [
 *     { id: 34, disabled: false, type: 'account' }
 *   ],
 *   service: [
 *     { id: 'facebook', disabled: false, type: 'service' }
 *   ],
 *   labels: {
 *     111: [
 *       { id: 1, disabled: false, type: 'labels' },
 *       { id: 2, disabled: false, type: 'labels' },
 *     ],
 *   },
 *   status: [
 *     { id: 'draft', disabled: true, type: 'account' },
 *     { id: 'for_approval', disabled: false, type: 'account' },
 *   ],
 * }
 *
 * MomentFilter also handles filterValues, which are used
 * to filter by dynamic, non-relational attributes (e.g. date
 * ranges). They are passed in as a standard key/value pair.
 *
 */
const MomentFilter = {}

/*
 * Maps an entity property to other properties to easily
 * determine inclusivity and exclusivity across multiple
 * property types. The values determine any other property
 * that the key should be inclusive with.
 *
 * @see `MomentFilter.single` for discussion of `inherited_labels`.
 */
const inclusivityMap = {
  account: ['service', 'account'],
  duration_starts_at: ['duration_starts_at'],
  duration_ends_at: ['duration_ends_at'],
  inherited_labels: ['labels', 'inherited_labels'],
  labels: ['labels', 'inherited_labels'],
  service: ['service', 'account'],
  status: ['status'],
}

/*
 * Takes a relation property and returns an array of all
 * of its ids.
 */
const getRelationIds = (relation) => {
  if (!relation) return []

  if (Array.isArray(relation)) {
    return relation.map((aRelation) => aRelation.id)
  }

  if (relation.type === 'service') {
    return [relation.system_name]
  }

  return [relation.id || relation]
}

/*
 * determines if an entity should be visible based on a
 * set of ids of one of its properties e.g. "label", and
 * if any of those ids are present in an array of
 * selected filter ids.
 */
const hasMatchingProperty = (relationIds, filter) => {
  if (!relationIds.length && filter) return false
  // Coerce both sides: filter ids arrive as numbers (e.g. label/account ids)
  // while JSON:API entity relation ids are strings, so `===` would never match.
  return relationIds.some((id) => String(filter.id) === String(id))
}

/*
 * Takes an entity and determines if it matches the
 * specified filter type (e.g. 'labels' or 'account').
 */
const matchesFilterType = (entity, filterType, filterGroup) => {
  const entityRelationIds = getRelationIds(entity[filterType])

  // Filters in the same group are considered inclusive.
  // e.g. multiple accounts selected.
  if (Array.isArray(filterGroup)) {
    return filterGroup.some((filter) =>
      hasMatchingProperty(entityRelationIds, filter)
    )
  }

  // Filters in an object are considered exclusive.
  // this primarily coveres label sets, where all
  // labels are of type 'labels' but each label set
  // is considered exclusive. See top of file for
  // and example.
  const matchesFilterGroup = {}
  Object.entries(filterGroup).forEach(([groupId, filters]) => {
    filters.forEach((filter) => {
      if (matchesFilterGroup[groupId]) return
      matchesFilterGroup[groupId] = hasMatchingProperty(
        entityRelationIds,
        filter
      )
    })
  })

  const matchingFilterGroups = []
  Object.keys(matchesFilterGroup).map((key) =>
    matchingFilterGroups.push(matchesFilterGroup[key])
  )

  return matchingFilterGroups.every((value) => value)
}

/*
 * Filters a moment based on a filter value,
 * which is a dynamic value -- not a relationship
 * like with other types of filtering. As is such,
 * each filter type may have custom matching logic.
 */
const matchesFilterValue = (moment, filter, filterValues) => {
  let matchesFilter = false

  switch (filter) {
    case 'duration_starts_at':
    case 'duration_ends_at': {
      let filterStartsAt = parseISO(
        filterValues.duration_starts_at || filterValues.duration_ends_at
      )
      const filterEndsAt = filterValues.duration_ends_at
        ? parseISO(filterValues.duration_ends_at)
        : filterStartsAt

      // Ensure the start date isn't after the end date
      filterStartsAt =
        filterStartsAt > filterEndsAt ? filterEndsAt : filterStartsAt

      // Exact date matches din't overlap the range, so bump them a second.
      const scheduledAt = subSeconds(parseISO(moment.scheduled_at), 1)
      const scheduledEndAt = addSeconds(
        parseISO(moment.scheduled_end_at || moment.scheduled_at),
        1
      )

      matchesFilter = areIntervalsOverlapping(
        { start: filterStartsAt, end: filterEndsAt },
        { start: scheduledAt, end: scheduledEndAt }
      )

      break
    }
    default:
      break
  }

  return matchesFilter
}

/*
 * Core filter pipeline shared by `MomentFilter.single` (moment-first
 * matching, also falls back to child posts) and `contentMatchesFilters`
 * (content-first, no fallback). Flags toggle the moment-specific behavior
 * so the two callers stay distinct in their semantics without duplicating
 * the inclusivity loop and inherited-labels handling.
 *
 *   - `checkChildPosts`: also try matching `entity.posts` for each filter
 *     type. Moment-only — content has no children.
 *   - `treatDurationAsPassing`: skip the date-range `matchesFilterValue`
 *     check entirely (content has scheduled_at but no duration).
 *   - `applyIdeaFallback`: a "moment with zero posts" auto-passes any
 *     filter group containing an `idea` filter id. Moment-only.
 */
const matchesFilters = (
  entity,
  {
    selectedFilters,
    filterValues,
    checkChildPosts = false,
    treatDurationAsPassing = false,
    applyIdeaFallback = false,
  }
) => {
  if (
    !Object.entries(selectedFilters).length &&
    !Object.entries(filterValues).length
  ) {
    return true
  }

  /**
   * Posts have an `inherited_labels` array with label associations inherited
   * from a story, moment, or account. If the post has labels directly assigned
   * `inherited_labels` will always be empty. Mirroring the `labels` filter onto
   * `inherited_labels` makes the filtering return posts whose only label
   * associations come from the account, which matches user expectations. Copy
   * rather than assign onto `selectedFilters` — it's the shared Redux filter
   * object and must not be mutated.
   */
  const effectiveFilters = selectedFilters.labels
    ? { ...selectedFilters, inherited_labels: selectedFilters.labels }
    : selectedFilters

  const typesToVerify = {}

  Object.entries(effectiveFilters).forEach(([filterType, filterGroup]) => {
    if (!Object.entries(filterGroup).length) return

    if (applyIdeaFallback) {
      const entityHasZeroPosts = !entity.posts || !entity.posts.length
      if (
        entityHasZeroPosts &&
        Array.isArray(filterGroup) &&
        filterGroup.some((filter) => filter.id === 'idea')
      ) {
        typesToVerify[filterType] = true
        return
      }
    }

    typesToVerify[filterType] = matchesFilterType(
      entity,
      filterType,
      filterGroup
    )

    if (!checkChildPosts || !entity.posts) return

    entity.posts.forEach((content) => {
      if (typesToVerify[filterType]) return
      typesToVerify[filterType] = matchesFilterType(
        content,
        filterType,
        filterGroup
      )
    })
  })

  Object.entries(filterValues).forEach(([filter]) => {
    typesToVerify[filter] = treatDurationAsPassing
      ? true
      : matchesFilterValue(entity, filter, filterValues)
  })

  // Checks verified filter types against their inclusive types. If any of
  // the inclusive types are true we consider this a match. Unknown keys
  // (not in `inclusivityMap`) skip rather than reject so a future filter
  // type that hasn't been wired into the map doesn't silently drop every
  // entity.
  return Object.keys(typesToVerify)
    .filter((key) => inclusivityMap[key])
    .every((key) =>
      inclusivityMap[key].some((attribute) => typesToVerify[attribute])
    )
}

/*
 * Takes a single moment object and a filter object
 * to determine if the moment matches the active filters.
 * 'selectedFilters' is an object of ALL active filters.
 * 'filterValues' is an object of ALL active filter values.
 */
MomentFilter.single = ({
  moment = {},
  selectedFilters = {},
  filterValues = {},
}) =>
  matchesFilters(moment, {
    selectedFilters,
    filterValues,
    checkChildPosts: true,
    applyIdeaFallback: true,
  })

/*
 * Responsible for removing any individually disabled
 * filters from all filter groups to ensure we do not
 * match against them.
 */
const removeDisabledFilters = (filterGroups) => {
  const filterGroupsWithoutDisabled = {}

  Object.entries(filterGroups).forEach(([filterType, filterGroup]) => {
    if (Array.isArray(filterGroup)) {
      const withoutDisabled = filterGroup.filter((filter) => !filter.disabled)
      if (withoutDisabled.length) {
        filterGroupsWithoutDisabled[filterType] = withoutDisabled
      }
      return
    }

    Object.entries(filterGroup).forEach(([filterGroupId, filter]) => {
      const withoutDisabled = filter.filter((f) => !f.disabled)
      if (withoutDisabled.length) {
        filterGroupsWithoutDisabled[filterType] =
          filterGroupsWithoutDisabled[filterType] || {}
        filterGroupsWithoutDisabled[filterType][filterGroupId] = withoutDisabled
      }
    })
  })

  return filterGroupsWithoutDisabled
}

/*
 * Responsible for removing any individually disabled
 * filter values to ensure we do not match against them.
 */
const removeDisabledValues = ({ filterValues, disabledValues }) => {
  const filterValuesWithoutDisabled = {}
  Object.entries(filterValues).forEach(([filter, value]) => {
    if (!disabledValues.includes(filter)) {
      filterValuesWithoutDisabled[filter] = value
    }
  })

  return filterValuesWithoutDisabled
}

/*
 * Takes an array of moment objects and a filter object
 * to determine which moments match all of the active
 * filters. "filter" is an object of ALL active filters.
 */
MomentFilter.inBulk = ({
  moments,
  selectedFilters = {},
  filterValues = {},
  disabledValues = [],
}) => {
  if (!moments) return {}
  const withoutDisabledFilters = removeDisabledFilters(selectedFilters)
  const withoutDisabledValues = removeDisabledValues({
    filterValues,
    disabledValues,
  })
  const result = {}

  Object.values(moments).forEach((moment) => {
    if (
      MomentFilter.single({
        moment,
        selectedFilters: withoutDisabledFilters,
        filterValues: withoutDisabledValues,
      })
    ) {
      result[moment.id] = moment
    }
  })
  return result
}

/*
 * Content-first filter. Unlike `MomentFilter.single`, there is no fallback
 * to the parent moment — a content item must match the filters on its own
 * attributes. Used by Week View's Content mode so that e.g. a Facebook
 * filter surfaces only the Facebook content on a multi-channel moment,
 * not every post under that moment. Duration filters are moment-level so
 * we treat them as always-passing here.
 */
const contentMatchesFilters = ({
  content = {},
  selectedFilters = {},
  filterValues = {},
}) =>
  matchesFilters(content, {
    selectedFilters,
    filterValues,
    treatDurationAsPassing: true,
  })

/*
 * Bulk variant of `contentMatchesFilters`. Shape mirrors `inBulk`.
 */
MomentFilter.contentInBulk = ({
  content,
  selectedFilters = {},
  filterValues = {},
  disabledValues = [],
}) => {
  if (!content) return {}
  const withoutDisabledFilters = removeDisabledFilters(selectedFilters)
  const withoutDisabledValues = removeDisabledValues({
    filterValues,
    disabledValues,
  })
  const result = {}

  Object.values(content).forEach((item) => {
    if (
      contentMatchesFilters({
        content: item,
        selectedFilters: withoutDisabledFilters,
        filterValues: withoutDisabledValues,
      })
    ) {
      result[item.id] = item
    }
  })
  return result
}
export default MomentFilter
